/**
 * Revenue KPI Card Component
 *
 * Temporary duplicate of the shared KPI card so Revenue can evolve its own
 * expanded layout without coupling new behavior into the generic KPI shell.
 */

interface RevenueKpiCardProps {
    title: string;
    description: string;
    value?: number | null;
    format: "percent" | "currency" | "number" | "days";
    isLoading?: boolean;
    icon?: string;
    showSign?: boolean;

    isExpandable?: boolean;
    isExpanded?: boolean;
    onToggleExpand?: () => void;
    expandedContent?: React.ReactNode;
    expandedHeaderAside?: React.ReactNode;
    isInteractive?: boolean;
    isActive?: boolean;
    onClick?: () => void;
    actionLabel?: string;
    onMouseEnter?: () => void;
}

export default function RevenueKpiCard({
    title,
    description,
    value,
    format,
    isLoading = false,
    icon = "fa-chart-simple",
    showSign = false,
    isExpandable = false,
    isExpanded = false,
    onToggleExpand,
    expandedContent,
    expandedHeaderAside,
    isInteractive = false,
    isActive = false,
    onClick,
    actionLabel,
    onMouseEnter,
}: RevenueKpiCardProps) {
    const formatValue = (val: number | null | undefined): string => {
        if (val === null || val === undefined) return "N/A";

        switch (format) {
            case "percent":
                const sign = showSign && val > 0 ? "+" : "";
                return `${sign}${val.toFixed(1)}%`;
            case "currency":
                return val.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                    currencyDisplay: "symbol",
                    maximumFractionDigits: 0,
                });
            case "days":
                return `${val.toFixed(1)} days`;
            default:
                return val.toLocaleString("en-US", { maximumFractionDigits: 2 });
        }
    };

    const handleCardClick = () => {
        if (isExpandable && !isExpanded) {
            onToggleExpand?.();
            return;
        }

        if (isInteractive) {
            onClick?.();
        }
    };

    const isClickable = (isExpandable && !isExpanded) || isInteractive;

    return (
        <div
            className={`rounded-sm border bg-white shadow-default dark:bg-boxdark transition-all duration-300 ${
                isExpanded ? "col-span-full" : ""
            } ${
                isActive
                    ? "border-primary ring-1 ring-primary/30 dark:border-primary"
                    : "border-stroke dark:border-strokedark"
            } ${isClickable ? "hover:-translate-y-0.5 hover:shadow-lg" : ""}`}
            onMouseEnter={onMouseEnter}
        >
            <div
                className={`p-6 ${isClickable ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-meta-4" : ""}`}
                onClick={isClickable ? handleCardClick : undefined}
            >
                <div className="flex items-center justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-meta-2 dark:bg-meta-4">
                        <i className={`fa-light ${icon} fa-lg text-primary`} />
                    </div>

                    <div className="flex items-center gap-2">
                        {isExpandable && (
                            <button
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onToggleExpand?.();
                                }}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-stroke text-sm font-semibold text-bodydark2 transition-colors hover:border-primary hover:text-primary dark:border-strokedark"
                                aria-label={isExpanded ? `Collapse ${title}` : `Expand ${title}`}
                            >
                                {isExpanded ? "-" : "+"}
                            </button>
                        )}
                        {!isExpandable && isInteractive && (
                            <i className="fa-light fa-arrow-right text-sm text-bodydark2" />
                        )}
                    </div>
                </div>

                <div
                    className={`mt-4 ${
                        isExpanded && expandedHeaderAside
                            ? "flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between"
                            : ""
                    }`}
                >
                    <div className="min-w-0">
                        <h4 className="text-xl font-bold text-black dark:text-white">
                            {isLoading ? (
                                <div className="h-7 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                            ) : (
                                formatValue(value)
                            )}
                        </h4>
                        <p className="mt-1 text-sm font-medium text-black dark:text-white">{title}</p>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>
                    </div>

                    {isExpanded && expandedHeaderAside && (
                        <div className="w-full xl:max-w-[360px] xl:flex-shrink-0">{expandedHeaderAside}</div>
                    )}
                </div>

                {isInteractive && actionLabel && (
                    <div className="mt-4 border-t border-stroke pt-4 dark:border-strokedark">
                        <div className="flex items-center justify-between text-xs">
                            <span className="font-medium text-primary">{actionLabel}</span>
                            <i className="fa-light fa-arrow-right text-primary" />
                        </div>
                    </div>
                )}
            </div>

            {isExpandable && isExpanded && expandedContent && (
                <div className="border-t border-stroke px-6 pb-6 pt-6 dark:border-strokedark">
                    {expandedContent}
                </div>
            )}
        </div>
    );
}
